import { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Camera, Loader2, X } from "lucide-react";
import WorkHoursEditor from "@/components/admin/WorkHoursEditor";
import { Card } from "@/components/ui/card";

export default function TrainerFormDialog({ trainer, onClose, onSaved }) {
  const isEdit = !!trainer;
  const [form, setForm] = useState({
    name: trainer?.name || "",
    email: trainer?.email || "",
    phone: trainer?.phone || "",
    specialization: trainer?.specialization || "",
    bio: trainer?.bio || "",
    is_active: trainer?.is_active ?? true,
    photo_url: trainer?.photo_url || "",
    hourly_rate: trainer?.hourly_rate || 0,
    work_hours: trainer?.work_hours || {},
    user_id: trainer?.user_id || "",
  });
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const fileInputRef = useRef(null);

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
  });

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const filteredUsers = users.filter((u) =>
    u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectUser = (user) => {
    set("name", user.full_name);
    set("email", user.email);
    set("user_id", user.id);
    setSearchQuery("");
    setShowUserDropdown(false);
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    const { file_url } = await base44.integrations.Core.UploadFile({ file });
    set("photo_url", file_url);
    setUploadingPhoto(false);
  };

  const handleSave = async () => {
    if (!form.name || !form.user_id) return;
    setLoading(true);
    if (isEdit) {
      await base44.entities.Trainer.update(trainer.id, form);
    } else {
      await base44.entities.Trainer.create(form);
    }
    setLoading(false);
    onSaved();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Trainer" : "Add Trainer"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2 overflow-y-auto pr-1">
          {/* Profile Photo */}
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-full bg-muted flex items-center justify-center overflow-hidden shrink-0 cursor-pointer border-2 border-dashed border-border hover:border-primary transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {form.photo_url ? (
                <img src={form.photo_url} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <Camera className="w-6 h-6 text-muted-foreground" />
              )}
            </div>
            <div>
              <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto}>
                {uploadingPhoto ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</> : "Upload Photo"}
              </Button>
              <p className="text-xs text-muted-foreground mt-1">Click photo or button to upload</p>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
          </div>

          <div className="space-y-1.5 relative">
            <Label>Name * {form.user_id && <span className="text-xs text-primary ml-2">(Linked to user {form.user_id})</span>}</Label>
            <Input
              value={searchQuery || form.name}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowUserDropdown(true);
              }}
              onFocus={() => setShowUserDropdown(true)}
              placeholder="Search and select a user"
              autoComplete="off"
            />
            {showUserDropdown && searchQuery && (
              <Card className="absolute top-full left-0 right-0 mt-1 z-10 max-h-48 overflow-y-auto border shadow-lg">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleSelectUser(user)}
                      className="w-full text-left px-4 py-2 hover:bg-muted transition-colors border-b last:border-b-0"
                    >
                      <p className="font-medium text-sm">{user.full_name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </button>
                  ))
                ) : (
                  <p className="px-4 py-2 text-xs text-muted-foreground">No users found</p>
                )}
              </Card>
            )}
            {form.user_id && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-1 top-7 h-8 w-8 p-0"
                onClick={() => {
                  set("name", "");
                  set("email", "");
                  set("user_id", "");
                  setSearchQuery("");
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="trainer@gym.com" />
          </div>
          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 234 567 8900" />
          </div>
          <div className="space-y-1.5">
            <Label>Specialization</Label>
            <Input value={form.specialization} onChange={(e) => set("specialization", e.target.value)} placeholder="e.g. Strength, HIIT, Yoga" />
          </div>
          <div className="space-y-1.5">
            <Label>Hourly Rate (USD)</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
              <Input
                type="number"
                min="0"
                step="5"
                value={form.hourly_rate || ""}
                onChange={(e) => set("hourly_rate", parseFloat(e.target.value) || 0)}
                placeholder="e.g. 75"
                className="pl-6"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Bio</Label>
            <Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="Short bio…" rows={2} />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} />
            <Label>Active</Label>
          </div>
          <WorkHoursEditor value={form.work_hours} onChange={(v) => set("work_hours", v)} />
          <div className="flex justify-end gap-2 pt-1">
           <Button variant="outline" onClick={onClose}>Cancel</Button>
           <Button onClick={handleSave} disabled={loading || !form.name || !form.user_id}>
             {loading ? "Saving…" : isEdit ? "Save Changes" : "Add Trainer"}
           </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}